<template>
  <v-app>
    <mainMenu></mainMenu>
    <v-container fluid>
      <v-card class="mb-4">
        <v-toolbar color="orange accent-3" dark dense flat>
          <v-toolbar-title>ค้นหาการจอง</v-toolbar-title>
          <v-spacer />
          <span class="caption">ค้นหาได้ทุกการจอง ด้วยเบอร์โทรหรือเลขที่ SO</span>
        </v-toolbar>
        <v-card-text>
          <v-row dense align="center">
            <v-col cols="12" sm="8" md="6">
              <v-text-field v-model="query" label="เบอร์โทร หรือ เลขที่ SO" clearable autofocus
                            color="orange accent-3" hide-details="auto"
                            placeholder="081-234-5678 หรือ 124120567" @keydown.enter="run" />
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <v-btn color="orange accent-3" dark :loading="loading" @click="run">ค้นหา</v-btn>
            </v-col>
          </v-row>
          <v-alert v-if="message" dense text :type="messageType" class="mt-3 mb-0">{{ message }}</v-alert>
          <v-alert v-if="failedNote" dense text type="warning" class="mt-3 mb-0">{{ failedNote }}</v-alert>
          <v-alert v-if="truncated" dense text type="warning" class="mt-3 mb-0">
            พบรายการจำนวนมาก ผลอาจไม่ครบ — ลองค้นหาด้วยเลขที่ SO
          </v-alert>
        </v-card-text>
      </v-card>

      <div v-if="recent.length" class="caption mb-2">ผลการค้นหา {{ recent.length + older.length }} รายการ</div>
      <order-card v-for="o in recent" :key="o.key" :order="o" :action="actionFor(o)" :attachable="attachable(o)"
                  @manage="manage" @book="book" @propose="propose" @attach="attach" />

      <v-expansion-panels v-if="older.length" class="mt-2">
        <v-expansion-panel>
          <v-expansion-panel-header>ประวัติเก่า ({{ older.length }})</v-expansion-panel-header>
          <v-expansion-panel-content>
            <order-card v-for="o in older" :key="o.key" :order="o" :action="actionFor(o)" :attachable="attachable(o)"
                        @manage="manage" @book="book" @propose="propose" @attach="attach" />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <proposal-dialog v-model="proposing.open" :order="proposing.order" :kind="proposing.kind" @saved="run" />
    </v-container>
  </v-app>
</template>

<script>
// ค้นหาการจอง -- "any customer, to answer them". Read-only: every write stays
// on จองวันส่งสินค้า, which this page links to. Rules in orderSearch.js, reads in
// searchService.js; see docs/superpowers/specs/2026-09-21-order-search-design.md.
import VueRouter from 'vue-router'
import R from '@/components/bookingRules'
import O from '@/components/orderSearch'
import { searchOrders } from '@/components/searchService'
import { reportError } from '@/components/report'
import mainMenu from '@/components/mainMenu'
import orderCard from '@/components/orderCard'
import proposalDialog from '@/components/proposalDialog'

const PART_LABEL = { slot: 'การจอง', booking: 'แผนจัดรถ', list: 'การจัดรถ', stop: 'สถานะคนขับ' }

export default {
  name: 'orderSearch',
  components: { mainMenu, orderCard, proposalDialog },
  data () {
    return {
      query: '',
      loading: false,
      recent: [],
      older: [],
      failed: [],
      truncated: false,
      message: '',
      messageType: 'info',
      proposing: { open: false, order: null, kind: 'move' }
    }
  },
  computed: {
    webPath () { return this.$store.getters.webPath },
    role () { return this.$store.state.status },
    identities () { return this.$store.getters.identities },
    // SAP's own failure is said in `message`, where it replaces ไม่พบ.
    failedNote () {
      const parts = this.failed.filter(f => PART_LABEL[f]).map(f => PART_LABEL[f])
      return parts.length ? `โหลดข้อมูล${parts.join(', ')}ไม่ได้ — ผลอาจไม่ครบ` : ''
    }
  },
  mounted () {
    const q = this.$route.query && this.$route.query.q
    if (q) {
      this.query = String(q)
      this.run()
    }
  },
  methods: {
    say (text, type) {
      this.message = text
      this.messageType = type || 'info'
    },
    clearResults () {
      this.recent = []
      this.older = []
      this.failed = []
      this.truncated = false
      this.message = ''
    },
    async run () {
      const q = O.classifyQuery(this.query)
      this.clearResults()
      if (q.kind === 'empty') return this.say('โปรดกรอกเบอร์โทรหรือเลขที่ SO')
      if (q.kind === 'short') return this.say(`เบอร์โทรต้องมีอย่างน้อย ${O.MIN_TEL_DIGITS} หลัก`, 'warning')
      this.keepInUrl()
      this.loading = true
      try {
        const r = await searchOrders(this.query, { webPath: this.webPath })
        const split = O.splitHistory(r.orders, R.todayStr())
        this.recent = split.recent
        this.older = split.older
        this.failed = r.failed
        this.truncated = r.truncated
        if (!r.orders.length) {
          if (r.failed.indexOf('sap') !== -1) this.say('ตรวจสอบกับ SAP ไม่ได้ โปรดลองใหม่', 'warning')
          else if (!r.failed.length) this.say('ไม่พบการจองหรือเลขที่ SO นี้')
        }
      } catch (e) {
        reportError('ค้นหาการจอง failed:', e)
        this.say('ค้นหาไม่สำเร็จ โปรดลองใหม่', 'error')
      } finally {
        this.loading = false
      }
    },
    // So a refresh, or Back from จองวันส่งสินค้า, shows the same search.
    keepInUrl () {
      const q = String(this.query || '').trim()
      if ((this.$route.query && this.$route.query.q) === q) return
      this.$router.replace({ name: 'orderSearch', query: { q } }).catch(err => {
        if (!VueRouter.isNavigationFailure(err)) reportError('ค้นหาการจอง: keeping the query failed:', err)
      })
    },
    actionFor (order) {
      return O.actionFor(order, this.role, this.identities)
    },
    propose (order, kind) {
      this.proposing = { open: true, order, kind }
    },
    manage (order) {
      this.$router.push({ name: 'bookingDate', query: { q: O.manageQuery(order) } })
    },
    book (order) {
      this.$router.push({ name: 'bookingDate', query: { bookSo: order.soNo } })
    },
    attachable (order) {
      return O.canAttachSo(order, {
        role: this.role,
        identities: this.identities,
        user: this.$store.state.user || '',
        profiles: this.$store.state.profiles
      })
    },
    // The ใส่ SO dialog lives on จองวันส่งสินค้า, beside the one fetchSo every SO
    // check runs through; a copy here is how those checks would drift apart.
    attach (order) {
      this.$router.push({ name: 'bookingDate', query: { attachSo: order.slotId } })
    }
  }
}
</script>
